'use client';
import EveningNote from '@/components/EveningNote';
import { useEffect, useState } from 'react';
import Link from 'next/link';
import WaSmartButton from './WaSmartButton';
import ContactButton from './ContactButton';
import ReviewsList, { type ReviewItem } from './ReviewsList';
import GalleryCarousel from './GalleryCarousel';
import AvatarZoom from './AvatarZoom';
import ProCustomerNote from './ProCustomerNote';
import { getContacted } from '@/lib/contactedState';
import { fmtRating } from '@/lib/format';
import { proAvailability, availLabel, type ProAvailInput } from '@/lib/availability';
import { useFitAvatar } from '@/lib/useFitAvatar';
import { avatarImg as avatarSet } from '@/lib/imgUrl';

// 30/09/26 (Founder): availability pill HIDDEN on category + pro pages (may come back) — flip to true to restore.
const SHOW_PRO_AVAILABILITY = false;

function AvailPill({ status: serverStatus, avail }: { status: 'today' | 'tomorrow' | 'week'; avail?: ProAvailInput }) {
  // 2026-09-28 (Founder): per-pro availability from the pro portal (lib/availability.ts proAvailability — the SAME function the
  // portal preview uses). First paint = the server's value; recomputed in the browser so a cached page is never stale.
  const [pa, setPa] = useState<{ status: string; label?: string }>(() => avail ? proAvailability(avail) : { status: serverStatus });
  useEffect(() => { if (avail) setPa(proAvailability(avail)); }, [avail?.availableFrom, avail?.hoursFrom, avail?.hoursTo]); // eslint-disable-line react-hooks/exhaustive-deps
  const status = pa.status;
  if (status === 'from') return (
    <span className="inline-flex items-center gap-[5px] text-sm px-2.5 py-[7px] md:gap-2 md:text-sm md:px-4 md:py-2 font-bold rounded-full whitespace-nowrap" style={{ background: '#f1f5f9', color: '#475569' }}>
      <span className="rounded-full flex-shrink-0 md:w-2 md:h-2" style={{ background: '#94a3b8', width: 7, height: 7 }} />{pa.label}
    </span>
  );
  const gd = avail?.gender; // he_professionals.gender → "פנוי" / "פנויה" / neutral "זמינות" (lib/gender.ts, 30/09/26)
  const c = status === 'today'
    ? { label: availLabel('today', gd), dot: '#22c55e', bg: '#dcfce7', color: '#14532d' }
    : status === 'week'
    ? { label: availLabel('week', gd), dot: '#f97316', bg: '#ffedd5', color: '#7c2d12' }
    : { label: availLabel('tomorrow', gd), dot: '#f59e0b', bg: '#fef9c3', color: '#713f12' };
  return (
    // Smaller than desktop (was a fixed text-sm/px-4/py-2 pill regardless of screen size) —
    // it was eating into the name/profession/location column's width on narrow screens.
    // Sized ~18% up from the initial mobile shrink per feedback, still well under the
    // md: desktop size where there's room to spare.
    <span className="inline-flex items-center gap-[5px] text-sm px-2.5 py-[7px] md:gap-2 md:text-sm md:px-4 md:py-2 font-bold rounded-full whitespace-nowrap"
      style={{ background: c.bg, color: c.color }}>
      <span className="rounded-full flex-shrink-0 md:w-2 md:h-2" style={{ background: c.dot, width: 7, height: 7 }} />
      {c.label}
    </span>
  );
}

type Props = {
  id: number;
  // 2026-08-23 (user request): made optional so this SAME component/engine can render
  // the pro's own header card on their own profile page too — linking a pro's photo/
  // name to their own current page is a no-op at best. Omit `href` there; pass
  // `zoomable` instead to get a click-to-enlarge photo (AvatarZoom) in its place.
  href?: string;
  firstName: string;
  fullName: string;
  phone?: string;
  whatsappValid?: number | null;
  imgSrc: string;
  rating10: number;
  reviewCount: number;
  /** word after the count — "המלצות" on the reviews-v2 preview (lib/reviewsPreview.ts reviewsWord) */
  reviewsLabel?: string;
  categorySlug: string;
  categoryName: string;
  locationStr: string;
  availStatus: 'today' | 'tomorrow' | 'week';
  avail?: ProAvailInput; // per-pro availability (2026-09-28)
  reviews?: ReviewItem[];
  // First card on the page (usually the LCP element) loads eagerly; every other card's
  // avatar is native-lazy so the browser doesn't fire off dozens of image requests at once
  // competing with the questionnaire/critical resources — same content, deferred fetch only.
  priority?: boolean;
  // Own-profile-page usage (2026-08-23): the name is the page's real content heading
  // there (SEO H1), not a decorative label like it is in a category listing — 'h3' stays
  // the default everywhere else.
  nameTag?: 'h1' | 'h2' | 'h3';
  // Own-profile-page usage: click-to-enlarge the photo instead of linking it anywhere
  // (there's nowhere sensible to link to on the pro's own page). Ignored if `href` is set.
  zoomable?: boolean;
  // Own-profile-page usage: that page already renders its own richer reviews section
  // (photos/tags) further down — this card's built-in ReviewsList would just duplicate it.
  hideReviews?: boolean;
  // Nationwide category: the location line reads "שירות בכל הארץ" instead of "<city> והסביבה".
  nationwide?: boolean;
  // Work-photo carousel rendered between the details header and the reviews (category gallery_relevant=1).
  gallery?: string[];
  /** IMAGE-SEO: Hebrew tag labels per gallery photo (aligned with `gallery`) + the pro's city (profile-photo alt only). */
  galleryTags?: string[][];
  proCity?: string | null;
  // PPC campaign mode (2026-09-23): a non-featured expert — rendered at low opacity, links
  // and buttons intact, with `dimNote` ("לא באשדוד") shown under the location line.
  dimmed?: boolean;
  dimNote?: string;
  /** Founder 2026-09-30: honest distance when the pro is NOT in the chosen city — "📍 12 ק״מ מרמלה" (lib/proMatching proCityDistanceNote). */
  distanceNote?: { km: number; city: string } | null;
  /** the pro's service area — the WhatsApp message names the visitor's city only if he serves it (required: null = list matching already did) */
  proArea: import('@/lib/proCityGate').ProArea | null;
  /** Founder 2026-09-28: the pro's own page on desktop — the whole card standing up (big photo → name/rating → reviews →
   *  category → location → availability → full-width contact), in the sticky side column, visible from the first paint. */
  vertical?: boolean;
  /** ?v=ct2 variant: the SAME contact components/handlers, rendered as one wide full-width row under the header. */
  ct2?: boolean;
  /** הערת מומחה ללקוח (Founder 30/09/26) — admin-set, shown under the contact buttons; empty → nothing. */
  customerNote?: string | null;
};

// Category expert card — ALSO reused verbatim (2026-08-23) as the pro's own header card
// on their profile page, so any future change to this card's structure/behavior applies
// everywhere at once. Desktop: WhatsApp/contact in a LEFT column, details + reviews on
// the right. Mobile: stacked (details → reviews → contact).
// Same components/handlers — only restyled (scoped to .ct2-contact; inline bg of the buttons is overridden with !important).
// ct2 contact pills beside the photo (1015c Nathan option B): solid emerald #0B8457 / royal #1E4FA0, >=46px tall, 122px wide fits 360px.
const CT2_SQ_CSS = `.ct2-sq>div{gap:8px!important}
.ct2-sq button{flex-direction:row!important;width:122px!important;height:auto!important;min-height:46px!important;padding:0 8px!important;gap:8px!important;border-radius:999px!important;white-space:nowrap;border:none!important;transition:filter .15s,transform .1s}
.ct2-sq button:first-child:not(:last-child){background:#0B8457!important}
.ct2-sq button:last-child{background:#1E4FA0!important}
.ct2-sq button:active{transform:scale(.98)}
.ct2-sq button svg{width:20px!important;height:20px!important;flex:none}
.ct2-sq button span{font-size:0!important;color:#fff!important}
.ct2-sq button span::after{font-size:13.5px;font-weight:700;line-height:1;color:#fff}
.ct2-sq button:first-child:not(:last-child) span::after{content:'הודעה'}
.ct2-sq button:last-child span::after{content:'התקשר'}`;

export default function CategoryProCard({
  id, href, firstName, fullName, phone, whatsappValid, imgSrc, rating10, reviewCount, reviewsLabel = 'המלצות',
  categorySlug, categoryName, locationStr, availStatus, avail, reviews = [], priority = false,
  nameTag = 'h3', zoomable = false, hideReviews = false, nationwide = false, gallery = [], galleryTags, proCity = null, customerNote = null,
  dimmed = false, dimNote, distanceNote, proArea, vertical = false, ct2 = false,
}: Props) {
  // Was THIS pro already contacted (message sent / call placed)? Once true, a
  // "get more offers" nudge appears below this specific card — the other cards
  // on this same category page already ARE the "other experts," no extra
  // widget/data needed. See lib/contactedState.ts.
  // Founder 2026-09-28 (screenshot, דודי/אשקלון): the photo spans exactly the text column — top of the name to the bottom of
  // the city line — never below it, on every phone.
  // M3 (Founder 02/10/26: "תמונות המומחים — להגדיל, לנצל שטח מת"): the photo is the text column + 14px (98 → 112 on a 4-line card),
  // the text column centred beside it, and the contact squares stacked in a 50px column (they used to sit side by side in 108px
  // with ~48px of dead space under them) — the freed width goes to the bigger photo and the text.
  const fitAv = useFitAvatar(112, { min: 112, max: 124, extra: 14 });
  const isNationwide = nationwide || !!(proArea as any)?.nationwide; // the card reads it itself — no caller can forget it (bughunter A5)
  const [sent, setSent] = useState(false);
  useEffect(() => {
    setSent(!!getContacted(id, phone));
    function onContacted(e: Event) {
      const detail = (e as CustomEvent).detail;
      if (detail?.proId === id) setSent(true);
    }
    window.addEventListener('experts:contacted', onContacted);
    return () => window.removeEventListener('experts:contacted', onContacted);
  }, [id, phone]);

  // 2026-08-24 (user request, screenshot-driven): "צפה בפרופיל" removed as its own
  // button — the photo and the name (see `header` below) are now BOTH links to the
  // same profile page, so a dedicated third button was redundant. Call is now its own
  // full-width row instead of splitting a row with the removed profile button.
  const contact = (
    // 2026-08-04 fix (user-caught, round 2): my first fix stacked contact ABOVE reviews
    // full-width on desktop — wrong call, the user wanted the ORIGINAL side-by-side
    // columns kept, just with the buttons top-aligned instead of vertically centered
    // (md:justify-center was the actual bug — it centered a short button stack inside
    // the taller stretched column, leaving empty space above the buttons). Reverted to
    // the two-column layout below; this div just needs to not re-center itself.
    <div className="md:h-full flex flex-col gap-2">
      {phone && (
        <WaSmartButton proArea={proArea} proName={fullName} proFirstName={firstName} proPhone={phone} whatsappValid={whatsappValid}
          proId={id} proGender={avail?.gender} categorySlug={categorySlug} categoryName={categoryName} />
      )}
      <ContactButton proName={fullName} proFirstName={firstName} proPhone={phone}
        proId={id} proGender={avail?.gender} categorySlug={categorySlug} categoryName={categoryName}
        className="flex items-center justify-center gap-1.5 py-2.5 px-2 rounded-xl font-bold text-white text-[15px]" />
      {phone && <EveningNote unique="pro-pair" />}
      {sent && (
        <div className="rounded-xl px-3 py-2.5 text-center" style={{ background: '#f0f9ff', border: '1px dashed #bae6fd' }}>
          <span className="text-xs font-bold" style={{ color: '#075985', lineHeight: 1.5 }}>
            🎉 קבלו עוד הצעות נוספות באותה קלות — עם ההודעה האישית שלכם, בחינם 👇
          </span>
        </div>
      )}
    </div>
  );

  // 2026-08-23 (user request, follow-up to the 2026-08-24 mobile column): the WA/call
  // buttons were still overflowing below the city row (stacked full-width `compact`
  // rows are taller than the 108px column has room for next to a 4-line text block).
  // Redesigned as two SQUARE buttons side by side — bigger icon on top, short label
  // below (no business name in the call label) — using WaSmartButton/ContactButton's
  // own `square` variants, so the combined status+buttons column never exceeds the
  // text column's height.
  const compactContact = (
    <div className="flex flex-col gap-1.5">
      {phone && (
        <WaSmartButton proArea={proArea} proName={fullName} proFirstName={firstName} proPhone={phone} whatsappValid={whatsappValid}
          proId={id} proGender={avail?.gender} categorySlug={categorySlug} categoryName={categoryName} square />
      )}
      <ContactButton proName={fullName} proFirstName={firstName} proPhone={phone}
        proId={id} proGender={avail?.gender} categorySlug={categorySlug} categoryName={categoryName} square />
    </div>
  );

  const header = (
    <div className="flex items-start gap-1 md:gap-3" style={{ minHeight: 40 }}>
      {/* ~15% bigger than the original 84px on desktop — always a perfect circle
          (fixed equal width/height + rounded-full + object-cover), never a
          square crop, so the source image's own square canvas never shows.
          Mobile: bumped 80px→92px (~15%) per feedback, measured against the 4-line
          text column's own height (~97.5px) so it still fits inside it — desktop is
          already at 97px vs. the same ~97.5px text height, i.e. already at the cap,
          so it's left as-is rather than pushed past the 4 lines' height. */}
      {(() => {
        const avatarImg = (
          <img {...avatarSet(imgSrc, 124, { kind: 'pro', ctx: { category: categoryName, proName: firstName, city: proCity } })} width={124} height={124}
            loading={priority ? 'eager' : 'lazy'} decoding="async"
            {...(priority ? { fetchPriority: 'high' } : {})}
            className="rounded-full object-cover"
            style={{ width: fitAv.size, height: fitAv.size, border: '2px solid #e0e7ff' }} />
        );
        if (href) return <Link href={href} className="flex-shrink-0">{avatarImg}</Link>;
        if (zoomable) return <div className="flex-shrink-0"><AvatarZoom src={imgSrc} name={firstName}>{avatarImg}</AvatarZoom></div>;
        return <div className="flex-shrink-0">{avatarImg}</div>;
      })()}

      {/* Name + details — takes the remaining row width (was sized-to-content, which let
          the availability pill's own flex-1 centering column squeeze it down to nothing
          on narrow screens and clip the location off the end of a shared, truncated line).
          Now 4 explicit stacked lines: name+rating, review count, profession, location —
          each gets its own line so nothing truncates, same font sizes throughout. */}
      <div className="min-w-0 flex-1 self-center" ref={fitAv.textRef}>
        {(() => {
          // 2026-07-31: was <h2> on category pages — a GEO/SEO audit flagged this as
          // the ONLY heading content on category hub pages (professional first names,
          // zero topical value), competing with the page's real H1 added in
          // CategoryPageBody. A name is not a content heading THERE; demoted to h3
          // (purely semantic, same visual style). On the pro's OWN profile page,
          // though, this name genuinely IS the page's main heading — `nameTag="h1"`
          // lets that caller opt back into a real H1 without a second copy of this markup.
          const NameTag = nameTag;
          const nameLine = (
            <>
              <NameTag className="text-xl font-black leading-tight" style={{ color: '#0f172a' }}>{firstName}</NameTag>
              {/* ★ and its number never part (bughunter N1: "איתמר ★" / "9.64" split across lines) */}
              <span className="inline-flex items-center gap-1 whitespace-nowrap">
                <span style={{ color: '#f59e0b', fontSize: '1.3rem', lineHeight: 1 }}>★</span>
                <span className="font-black text-xl" style={{ color: '#0d47a1' }}>{fmtRating(rating10)}</span>
              </span>
            </>
          );
          return href
            ? <Link href={href} className="flex items-center gap-1 flex-wrap">{nameLine}</Link>
            : <div className="flex items-center gap-1 flex-wrap">{nameLine}</div>;
        })()}
        <div>
          <span className="text-sm" style={{ color: '#94a3b8' }}>(<bdi dir="ltr">{reviewCount}</bdi> {reviewsLabel})</span>
        </div>
        <p className="text-sm font-semibold mt-0.5" style={{ color: '#475569' }}>{categoryName}</p>
        {isNationwide ? (
          // Founder 2026-09-22: "יבנה ונותן שירות בכל הארץ" — the home city stays, the reach is stated
          <p className="text-sm font-medium mt-0.5" style={{ color: '#94a3b8' }}>{locationStr ? `${locationStr} ונותן שירות בכל הארץ` : 'נותן שירות בכל הארץ'}</p>
        ) : locationStr && (
          <p className="text-sm font-medium mt-0.5" style={{ color: '#94a3b8' }}>{locationStr}{/בלבד$/.test(locationStr) ? '' : ' והסביבה'}</p>
        )}
        {!isNationwide && distanceNote && (
          <p className="text-sm font-semibold mt-0.5" data-distance-note style={{ color: '#475569' }}><span aria-hidden="true">📍 </span><bdi dir="ltr">{distanceNote.km}</bdi> ק״מ מ{distanceNote.city}</p>
        )}

      </div>

      {/* 2026-08-24 (user request, full redesign of the mobile column): status +
          message + call now stack in their OWN narrow column beside the photo/text,
          parallel to the name/reviews/category rows, instead of their own full-width
          rows below the whole header — more compact overall, and "צפה בפרופיל" is gone
          as its own button (the photo and the name above are both already links to the
          same profile). Desktop (lg+) keeps its existing wide horizontal pill+button
          block, unchanged — this narrow stacked column is mobile/tablet-only.
          2026-08-23: status pill right-aligned (items-end, not stretch) — it's sized to
          its own content and narrower than the two 50px squares below it, so this uses
          the leftover width next to it more naturally instead of an artificial stretch. */}
      {!ct2 && <div className="flex-shrink-0 flex flex-col items-end gap-1.5 lg:hidden self-center">
        {SHOW_PRO_AVAILABILITY && <AvailPill status={availStatus} avail={avail} />}
        {compactContact}
      </div>}
      {/* ct2 (1015 Nathan): the contact squares sit in the free space beside the photo/name column (RTL: photo at the start,
          buttons at the far end) at every width — never under the photo. Same handlers (compactContact). */}
      {ct2 && <div className="ct2-sq flex-shrink-0 self-center"><style>{CT2_SQ_CSS}</style>{compactContact}</div>}

      {/* Availability + (desktop only) contact buttons — combined into the same row as
          the name/rating per explicit user request (2026-08-04, round 7): buttons were
          previously their own row below the header, which read as disconnected from the
          "who is this and are they available" info right above them. Fixed width so it
          never squeezes the name/profession/location column to its right. */}
      {!ct2 && <div className="flex-shrink-0 self-center hidden lg:flex items-center gap-3">
        {SHOW_PRO_AVAILABILITY && <AvailPill status={availStatus} avail={avail} />}
        {/* lg:, not md: — at the narrow end of md (768px) there isn't enough room left
            for the name/rating column once avatar + pill + a 420px button block are all
            in the same row (confirmed: text wraps badly at exactly 768px). */}
        <div className="lg:w-[420px]">{contact}</div>
      </div>}
    </div>
  );

  if (vertical) {
    const NameTag = nameTag;
    return (
      <div className="rounded-2xl overflow-hidden" data-card="vertical"
        style={{ background: '#fff', border: '1px solid #e2e8f0', boxShadow: '0 6px 24px rgba(13,71,161,.10)' }}>
        <div style={{ height: 4, background: 'linear-gradient(90deg,#0d47a1,#1976d2)' }} />
        <div className="p-4 flex flex-col items-center text-center gap-1.5">
          <AvatarZoom src={imgSrc} name={firstName}>
            <picture>{/* desktop-only card photo (md+): phones get a 1px blank so the hidden eager LCP image is never downloaded */}<source media="(max-width: 767px)" srcSet="data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==" /><img {...avatarSet(imgSrc, 240, { kind: 'pro', ctx: { category: categoryName, proName: firstName, city: proCity } })} width={240} height={240} loading={priority ? 'eager' : 'lazy'} decoding="async"
              {...(priority ? { fetchPriority: 'high' } : {})}
              className="object-cover rounded-full" style={{ width: 'min(100%, 220px, 26vh)', aspectRatio: '1 / 1', height: 'auto', border: '3px solid #e0e7ff', boxShadow: '0 8px 24px rgba(13,71,161,.14)' }} /></picture>
          </AvatarZoom>
          <div className="flex items-center justify-center gap-1.5 flex-wrap mt-2">
            <NameTag className="text-2xl font-black leading-tight" style={{ color: '#0f172a' }}>{firstName}</NameTag>
            <span style={{ color: '#f59e0b', fontSize: '1.4rem', lineHeight: 1 }}>★</span>
            <span className="font-black text-2xl" style={{ color: '#0d47a1' }}>{fmtRating(rating10)}</span>
          </div>
          <span className="text-sm" style={{ color: '#94a3b8' }}>(<bdi dir="ltr">{reviewCount}</bdi> {reviewsLabel})</span>
          <p className="text-base font-bold" style={{ color: '#475569' }}>{categoryName}</p>
          {isNationwide
            ? <p className="text-sm font-medium" style={{ color: '#94a3b8' }}>{locationStr ? `${locationStr} ונותן שירות בכל הארץ` : 'נותן שירות בכל הארץ'}</p>
            : locationStr && <p className="text-sm font-medium" style={{ color: '#94a3b8' }}>{locationStr}{/בלבד$/.test(locationStr) ? '' : ' והסביבה'}</p>}
          {SHOW_PRO_AVAILABILITY && <div className="mt-1"><AvailPill status={availStatus} avail={avail} /></div>}
          <ProCustomerNote note={customerNote} className="w-full mt-2" />
          <div id="pro-side-contact" className="w-full mt-2">{contact}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl overflow-hidden"
      data-dimmed={dimmed ? '1' : undefined}
      style={{ background: '#fff', border: '1px solid #e2e8f0', boxShadow: dimmed ? 'none' : '0 4px 24px rgba(13,71,161,.08)' }}>
      <div style={{ height: 4, background: 'linear-gradient(90deg,#0d47a1,#1976d2)' }} />

      {/* 2026-08-04 fix (round 7 — explicit user instruction): round 6 put the contact
          buttons in their own row directly under the header, which was closer but still
          read as a separate block rather than part of "who this is / are they
          available." Buttons are now merged into the header's own row, next to the
          availability pill (see the `header` block above) — genuinely the same row as
          the name/rating/availability, not just visually adjacent. Reviews render right
          after, completely unobstructed, 100% of the card's width.
          2026-08-24 (user request): the mobile "own row below the header" `contact`
          block is GONE — `compactContact` now lives INSIDE the header's own mobile
          column (see `header` above), so there's no separate full-width row left below
          it. Only ONE DOM instance of the WA/call actions renders per breakpoint now
          (compactContact on mobile inside `header`, `contact` on desktop also inside
          `header`) — no duplicate-instance concern the old comment here used to note. */}
      {dimNote && (
        // Campaign/city mode: the ONE new fact on a dimmed card stays fully readable —
        // the pill sits above the dimmed content, never inside the faded container.
        <div className="px-2.5 md:px-4 pt-2">
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold"
            style={{ background: '#fff7ed', color: '#9a3412', border: '1px solid #fdba74' }}>
            <span aria-hidden="true">📍</span>{dimNote}
          </span>
        </div>
      )}
      <div className="p-2.5 md:p-4 flex flex-col" style={{ opacity: dimmed ? 0.45 : 1, transition: 'opacity .3s ease' }}>
        <div>{header}</div>
        <ProCustomerNote note={customerNote} className="mt-2.5" />
        {gallery.length > 0 && (
          <div className="mt-3"><GalleryCarousel images={gallery} altBase={`${firstName} - ${categoryName}`} seoCtx={{ category: categoryName, service: categoryName, proName: firstName }} imageTags={galleryTags} /></div>
        )}
        {!hideReviews && (
          <div className="mt-3 md:mt-4">
            <ReviewsList reviews={reviews} proId={id} totalCount={reviewCount} />
          </div>
        )}
      </div>
    </div>
  );
}
